import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { NotificationContainer } from '../components/ui/Notification';
import { AuthContext } from '../App';
import { DollarSignIcon, CalendarIcon, MapPinIcon, FileTextIcon, CoffeeIcon, UtensilsIcon, WineIcon, CarIcon, PlaneIcon, TrainIcon, HomeIcon, ShoppingBagIcon, PlusIcon, UserIcon, XIcon, InfoIcon, AlertTriangleIcon, CheckIcon, UploadIcon, CameraIcon, ClipboardIcon, UsersIcon } from 'lucide-react';
import { mockPerDiemRates, mockEmployees } from '../utils/mockData';
export function ExpenseEntry() {
  const navigate = useNavigate();
  const {
    userRole
  } = useContext(AuthContext);
  const isAdmin = userRole === 'admin';
  // Form state
  const [expenseData, setExpenseData] = useState({
    date: new Date().toISOString().split('T')[0],
    location: 'Washington, DC',
    type: '',
    subtype: '',
    amount: '',
    description: '',
    attendees: [],
    receipt: null,
    receiptPreview: null,
    employeeId: isAdmin ? '' : '1' // Default to current user if not admin
  });
  // Validation state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [perDiemValidation, setPerDiemValidation] = useState(null);
  // Attendees state
  const [attendeeName, setAttendeeName] = useState('');
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  // Per diem rates for the current location and date
  const [perDiemRates, setPerDiemRates] = useState(null);
  // Fetch per diem rates when location or date changes
  useEffect(() => {
    if (expenseData.location && expenseData.date) {
      const rates = mockPerDiemRates.getPerDiemRateForDate(expenseData.location, new Date(expenseData.date));
      setPerDiemRates(rates);
    }
  }, [expenseData.location, expenseData.date]);
  // Validate expense against per diem when amount, type, or subtype changes
  useEffect(() => {
    if (expenseData.amount && expenseData.type && (expenseData.type !== 'meal' || expenseData.subtype) && expenseData.location && expenseData.date) {
      const amount = parseFloat(expenseData.amount);
      if (!isNaN(amount)) {
        const validation = mockPerDiemRates.validateExpenseAgainstPerDiem(expenseData.type, expenseData.subtype, amount, expenseData.location, new Date(expenseData.date));
        setPerDiemValidation(validation);
      }
    } else {
      setPerDiemValidation(null);
    }
  }, [expenseData.amount, expenseData.type, expenseData.subtype, expenseData.location, expenseData.date]);
  // Handle input change
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setExpenseData({
      ...expenseData,
      [name]: value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  // Handle expense type selection
  const handleTypeSelect = type => {
    setExpenseData({
      ...expenseData,
      type,
      // Reset subtype when changing type
      subtype: ''
    });
    // Clear error for type
    if (errors.type) {
      setErrors({
        ...errors,
        type: null
      });
    }
  };
  // Handle meal subtype selection
  const handleSubtypeSelect = subtype => {
    setExpenseData({
      ...expenseData,
      subtype
    });
    // Clear error for subtype
    if (errors.subtype) {
      setErrors({
        ...errors,
        subtype: null
      });
    }
  };
  // Handle receipt upload
  const handleReceiptUpload = e => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Create URL for preview
      const previewUrl = URL.createObjectURL(file);
      setExpenseData({
        ...expenseData,
        receipt: file,
        receiptPreview: previewUrl
      });
      // Clear error for receipt
      if (errors.receipt) {
        setErrors({
          ...errors,
          receipt: null
        });
      }
    }
  };
  // Handle adding attendee
  const handleAddAttendee = () => {
    if (attendeeName.trim()) {
      setExpenseData({
        ...expenseData,
        attendees: [...expenseData.attendees, attendeeName.trim()]
      });
      setAttendeeName('');
      // Clear error for attendees
      if (errors.attendees) {
        setErrors({
          ...errors,
          attendees: null
        });
      }
    }
  };
  // Handle removing attendee
  const handleRemoveAttendee = index => {
    const updatedAttendees = [...expenseData.attendees];
    updatedAttendees.splice(index, 1);
    setExpenseData({
      ...expenseData,
      attendees: updatedAttendees
    });
  };
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    if (!expenseData.date) newErrors.date = 'Date is required';
    if (!expenseData.location) newErrors.location = 'Location is required';
    if (!expenseData.type) newErrors.type = 'Expense type is required';
    if (expenseData.type === 'meal' && !expenseData.subtype) {
      newErrors.subtype = 'Meal type is required';
    }
    if (!expenseData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(parseFloat(expenseData.amount)) || parseFloat(expenseData.amount) <= 0) {
      newErrors.amount = 'Amount must be a positive number';
    }
    if (!expenseData.description || expenseData.description.trim() === '') {
      newErrors.description = 'Description is required';
    }
    // Validate receipt requirement based on amount
    const amount = parseFloat(expenseData.amount);
    if (!isNaN(amount) && amount > 75 && !expenseData.receipt) {
      newErrors.receipt = 'Receipt is required for expenses over $75';
    }
    // Validate attendees for meal expenses
    if (expenseData.type === 'meal' && expenseData.attendees.length === 0) {
      newErrors.attendees = 'At least one attendee is required for meal expenses';
    }
    // Validate employee selection for admin users
    if (isAdmin && !expenseData.employeeId) {
      newErrors.employeeId = 'Employee selection is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        // Add success notification
        addNotification({
          type: 'success',
          title: 'Expense Submitted',
          message: 'Your expense has been submitted successfully and is pending approval.'
        });
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate(isAdmin ? '/admin' : '/dashboard');
        }, 2000);
      }, 1500);
    }
  };
  // Add notification
  const addNotification = notification => {
    const id = Date.now();
    setNotifications(prev => [...prev, {
      id,
      ...notification
    }]);
  };
  // Remove notification
  const removeNotification = id => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };
  // Calculate per diem allowance for the selected expense type
  const getPerDiemAllowance = () => {
    if (!perDiemRates) return null;
    if (expenseData.type === 'meal' && expenseData.subtype) {
      return perDiemRates.mealBreakdown[expenseData.subtype];
    } else if (expenseData.type === 'lodging') {
      return perDiemRates.lodgingRate;
    }
    return null;
  };
  // Get employee name by ID
  const getEmployeeName = id => {
    const employee = mockEmployees.find(emp => emp.id.toString() === id);
    return employee ? employee.name : 'Unknown Employee';
  };
  // Filter employees for dropdown
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const filteredEmployees = mockEmployees.filter(employee => employee.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()));
  return <div className="max-w-4xl mx-auto" data-id="element-1696">
      {/* Notifications */}
      <NotificationContainer notifications={notifications} onClose={removeNotification} data-id="element-1697" />
      <div className="mb-6" data-id="element-1698">
        <h1 className="text-2xl font-bold text-gray-100" data-id="element-1699">
          {isAdmin ? 'Add Expense for Employee' : 'New Expense'}
        </h1>
        <p className="text-gray-400 mt-1" data-id="element-1700">
          {isAdmin ? 'Manually create an expense entry for an employee' : 'Submit a new expense for reimbursement'}
        </p>
      </div>
      <form onSubmit={handleSubmit} data-id="element-1701">
        <Card className="mb-6" data-id="element-1702">
          <div className="p-6 space-y-6" data-id="element-1703">
            {/* Employee Selection (Admin Only) */}
            {isAdmin && <div className="employee-search-container" data-id="element-1704">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1705">
                  Employee <span className="text-red-500" data-id="element-1706">*</span>
                </label>
                <div className="relative" data-id="element-1707">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1708">
                    <UsersIcon size={16} className="text-gray-400" data-id="element-1709" />
                  </div>
                  <input type="text" placeholder="Search employees..." className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${errors.employeeId ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} value={employeeSearchTerm} onChange={e => {
                setEmployeeSearchTerm(e.target.value);
                setShowEmployeeDropdown(true);
              }} onFocus={() => setShowEmployeeDropdown(true)} data-id="element-1710" />
                  {showEmployeeDropdown && <div className="absolute z-10 mt-1 w-full bg-gray-700 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto" data-id="element-1711">
                      {filteredEmployees.length > 0 ? filteredEmployees.map(employee => <div key={employee.id} className="px-4 py-2 hover:bg-gray-600 cursor-pointer" onClick={() => {
                  setExpenseData({
                    ...expenseData,
                    employeeId: employee.id.toString()
                  });
                  setEmployeeSearchTerm(employee.name);
                  setShowEmployeeDropdown(false);
                  // Clear error for employeeId
                  if (errors.employeeId) {
                    setErrors({
                      ...errors,
                      employeeId: null
                    });
                  }
                }} data-id="element-1712">
                            <div className="flex items-center" data-id="element-1713">
                              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-600 flex items-center justify-center text-xs text-gray-300 mr-2" data-id="element-1714">
                                {employee.name.split(' ').map(n => n[0]).join('')}
                              </div>
                              <div data-id="element-1715">
                                <div className="text-sm font-medium text-gray-200" data-id="element-1716">
                                  {employee.name}
                                </div>
                                <div className="text-xs text-gray-400" data-id="element-1717">
                                  {employee.department} -{' '}
                                  {employee.position || 'Employee'}
                                </div>
                              </div>
                            </div>
                          </div>) : <div className="px-4 py-2 text-sm text-gray-400" data-id="element-1718">
                          No employees found
                        </div>}
                    </div>}
                </div>
                {expenseData.employeeId && <div className="mt-2 flex items-center text-sm text-gray-300" data-id="element-1719">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-gray-600 flex items-center justify-center text-xs text-gray-300 mr-2" data-id="element-1720">
                      {getEmployeeName(expenseData.employeeId).split(' ').map(n => n[0]).join('')}
                    </div>
                    <span data-id="element-1721">
                      Selected: {getEmployeeName(expenseData.employeeId)}
                    </span>
                  </div>}
                {errors.employeeId && <p className="mt-1 text-sm text-red-500" data-id="element-1722">
                    {errors.employeeId}
                  </p>}
              </div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1723">
              <div data-id="element-1724">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1725">
                  Date <span className="text-red-500" data-id="element-1726">*</span>
                </label>
                <div className="relative" data-id="element-1727">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1728">
                    <CalendarIcon size={16} className="text-gray-400" data-id="element-1729" />
                  </div>
                  <input type="date" name="date" value={expenseData.date} onChange={handleChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${errors.date ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} required data-id="element-1730" />
                </div>
                {errors.date && <p className="mt-1 text-sm text-red-500" data-id="element-1731">{errors.date}</p>}
              </div>
              <div data-id="element-1732">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1733">
                  Location <span className="text-red-500" data-id="element-1734">*</span>
                </label>
                <div className="relative" data-id="element-1735">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1736">
                    <MapPinIcon size={16} className="text-gray-400" data-id="element-1737" />
                  </div>
                  <select name="location" value={expenseData.location} onChange={handleChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${errors.location ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} required data-id="element-1738">
                    <option value="Washington, DC" data-id="element-1739">Washington, DC</option>
                    <option value="New York, NY" data-id="element-1740">New York, NY</option>
                    <option value="San Francisco, CA" data-id="element-1741">San Francisco, CA</option>
                    <option value="Chicago, IL" data-id="element-1742">Chicago, IL</option>
                  </select>
                </div>
                {errors.location && <p className="mt-1 text-sm text-red-500" data-id="element-1743">{errors.location}</p>}
              </div>
            </div>
            <div data-id="element-1744">
              <label className="block text-sm font-medium text-gray-200 mb-3" data-id="element-1745">
                Expense Type <span className="text-red-500" data-id="element-1746">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-1747">
                <button type="button" className={`flex flex-col items-center justify-center p-4 rounded-md border ${expenseData.type === 'meal' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleTypeSelect('meal')} data-id="element-1748">
                  <UtensilsIcon size={24} className="mb-2" data-id="element-1749" />
                  <span className="text-sm font-medium" data-id="element-1750">Meal</span>
                </button>
                <button type="button" className={`flex flex-col items-center justify-center p-4 rounded-md border ${expenseData.type === 'transportation' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleTypeSelect('transportation')} data-id="element-1751">
                  <CarIcon size={24} className="mb-2" data-id="element-1752" />
                  <span className="text-sm font-medium" data-id="element-1753">Transportation</span>
                </button>
                <button type="button" className={`flex flex-col items-center justify-center p-4 rounded-md border ${expenseData.type === 'lodging' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleTypeSelect('lodging')} data-id="element-1754">
                  <HomeIcon size={24} className="mb-2" data-id="element-1755" />
                  <span className="text-sm font-medium" data-id="element-1756">Lodging</span>
                </button>
                <button type="button" className={`flex flex-col items-center justify-center p-4 rounded-md border ${expenseData.type === 'other' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleTypeSelect('other')} data-id="element-1757">
                  <ShoppingBagIcon size={24} className="mb-2" data-id="element-1758" />
                  <span className="text-sm font-medium" data-id="element-1759">Other</span>
                </button>
              </div>
              {errors.type && <p className="mt-1 text-sm text-red-500" data-id="element-1760">{errors.type}</p>}
            </div>
            {/* Meal Subtype */}
            {expenseData.type === 'meal' && <div data-id="element-1761">
                <label className="block text-sm font-medium text-gray-200 mb-3" data-id="element-1762">
                  Meal Type <span className="text-red-500" data-id="element-1763">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-1764">
                  <button type="button" className={`flex items-center p-3 rounded-md border ${expenseData.subtype === 'breakfast' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleSubtypeSelect('breakfast')} data-id="element-1765">
                    <CoffeeIcon size={18} className="mr-2" data-id="element-1766" />
                    <span className="text-sm" data-id="element-1767">Breakfast</span>
                  </button>
                  <button type="button" className={`flex items-center p-3 rounded-md border ${expenseData.subtype === 'lunch' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleSubtypeSelect('lunch')} data-id="element-1768">
                    <UtensilsIcon size={18} className="mr-2" data-id="element-1769" />
                    <span className="text-sm" data-id="element-1770">Lunch</span>
                  </button>
                  <button type="button" className={`flex items-center p-3 rounded-md border ${expenseData.subtype === 'dinner' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleSubtypeSelect('dinner')} data-id="element-1771">
                    <WineIcon size={18} className="mr-2" data-id="element-1772" />
                    <span className="text-sm" data-id="element-1773">Dinner</span>
                  </button>
                  <button type="button" className={`flex items-center p-3 rounded-md border ${expenseData.subtype === 'incidentals' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleSubtypeSelect('incidentals')} data-id="element-1774">
                    <ShoppingBagIcon size={18} className="mr-2" data-id="element-1775" />
                    <span className="text-sm" data-id="element-1776">Incidentals</span>
                  </button>
                </div>
                {errors.subtype && <p className="mt-1 text-sm text-red-500" data-id="element-1777">{errors.subtype}</p>}
              </div>}
            {/* Transportation Subtype */}
            {expenseData.type === 'transportation' && <div data-id="element-1778">
                <label className="block text-sm font-medium text-gray-200 mb-3" data-id="element-1779">
                  Transportation Type
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-1780">
                  <button type="button" className={`flex items-center p-3 rounded-md border ${expenseData.subtype === 'taxi' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleSubtypeSelect('taxi')} data-id="element-1781">
                    <CarIcon size={18} className="mr-2" data-id="element-1782" />
                    <span className="text-sm" data-id="element-1783">Taxi/Rideshare</span>
                  </button>
                  <button type="button" className={`flex items-center p-3 rounded-md border ${expenseData.subtype === 'airfare' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleSubtypeSelect('airfare')} data-id="element-1784">
                    <PlaneIcon size={18} className="mr-2" data-id="element-1785" />
                    <span className="text-sm" data-id="element-1786">Airfare</span>
                  </button>
                  <button type="button" className={`flex items-center p-3 rounded-md border ${expenseData.subtype === 'train' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleSubtypeSelect('train')} data-id="element-1787">
                    <TrainIcon size={18} className="mr-2" data-id="element-1788" />
                    <span className="text-sm" data-id="element-1789">Train</span>
                  </button>
                  <button type="button" className={`flex items-center p-3 rounded-md border ${expenseData.subtype === 'other' ? 'border-blue-500 bg-blue-900/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-650'} transition-colors`} onClick={() => handleSubtypeSelect('other')} data-id="element-1790">
                    <ShoppingBagIcon size={18} className="mr-2" data-id="element-1791" />
                    <span className="text-sm" data-id="element-1792">Other</span>
                  </button>
                </div>
              </div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1793">
              <div data-id="element-1794">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1795">
                  Amount <span className="text-red-500" data-id="element-1796">*</span>
                </label>
                <div className="relative" data-id="element-1797">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1798">
                    <DollarSignIcon size={16} className="text-gray-400" data-id="element-1799" />
                  </div>
                  <input type="text" name="amount" value={expenseData.amount} onChange={handleChange} placeholder="0.00" className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${errors.amount || perDiemValidation && !perDiemValidation.valid ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-600 focus:ring-blue-500 focus:border-blue-500'} rounded-md shadow-sm focus:outline-none focus:ring-2 text-gray-200`} required data-id="element-1800" />
                </div>
                {errors.amount && <p className="mt-1 text-sm text-red-500" data-id="element-1801">{errors.amount}</p>}
                {/* Per Diem Validation */}
                {perDiemValidation && !perDiemValidation.valid && <p className="mt-1 text-sm text-red-500" data-id="element-1802">
                    {perDiemValidation.message}
                  </p>}
                {/* Per Diem Allowance */}
                {perDiemRates && getPerDiemAllowance() !== null && <p className="mt-1 text-sm text-gray-400" data-id="element-1803">
                    Per diem allowance: ${getPerDiemAllowance()}
                  </p>}
              </div>
              <div data-id="element-1804">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1805">
                  Description <span className="text-red-500" data-id="element-1806">*</span>
                </label>
                <textarea name="description" value={expenseData.description} onChange={handleChange} rows={3} placeholder="Brief description of the expense..." className={`w-full px-3 py-2 bg-gray-700 border ${errors.description ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} required data-id="element-1807"></textarea>
                {errors.description && <p className="mt-1 text-sm text-red-500" data-id="element-1808">
                    {errors.description}
                  </p>}
              </div>
            </div>
            {/* Attendees (for meal expenses) */}
            {expenseData.type === 'meal' && <div data-id="element-1809">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1810">
                  Attendees <span className="text-red-500" data-id="element-1811">*</span>
                </label>
                <p className="text-xs text-gray-400 mb-2" data-id="element-1812">
                  List all attendees, including{' '}
                  {isAdmin ? getEmployeeName(expenseData.employeeId) : 'yourself'}
                </p>
                <div className="flex space-x-2 mb-2" data-id="element-1813">
                  <div className="relative flex-1" data-id="element-1814">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1815">
                      <UserIcon size={16} className="text-gray-400" data-id="element-1816" />
                    </div>
                    <input type="text" value={attendeeName} onChange={e => setAttendeeName(e.target.value)} placeholder="Enter attendee name" className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${errors.attendees ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} data-id="element-1817" />
                  </div>
                  <Button type="button" onClick={handleAddAttendee} disabled={!attendeeName.trim()} data-id="element-1818">
                    <PlusIcon size={16} className="mr-1.5" data-id="element-1819" />
                    Add
                  </Button>
                </div>
                {expenseData.attendees.length > 0 ? <div className="mt-2 flex flex-wrap gap-2" data-id="element-1820">
                    {expenseData.attendees.map((attendee, index) => <div key={index} className="flex items-center bg-gray-700 border border-gray-600 rounded-full px-3 py-1.5" data-id="element-1821">
                        <span className="text-sm text-gray-200" data-id="element-1822">
                          {attendee}
                        </span>
                        <button type="button" onClick={() => handleRemoveAttendee(index)} className="ml-2 text-gray-400 hover:text-gray-200" data-id="element-1823">
                          <XIcon size={14} data-id="element-1824" />
                        </button>
                      </div>)}
                  </div> : <p className="text-sm text-gray-400 italic" data-id="element-1825">
                    No attendees added
                  </p>}
                {errors.attendees && <p className="mt-1 text-sm text-red-500" data-id="element-1826">
                    {errors.attendees}
                  </p>}
              </div>}
            {/* Receipt Upload */}
            <div data-id="element-1827">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1828">
                Receipt Upload{' '}
                {parseFloat(expenseData.amount) > 75 && <span className="text-red-500" data-id="element-1829">*</span>}
              </label>
              <p className="text-xs text-gray-400 mb-2" data-id="element-1830">
                {parseFloat(expenseData.amount) > 75 ? 'Receipt is required for expenses over $75' : 'Receipt is recommended but not required for expenses under $75'}
              </p>
              {expenseData.receiptPreview ? <div className="border border-gray-600 rounded-md p-4 bg-gray-700" data-id="element-1831">
                  <div className="flex items-center justify-between mb-2" data-id="element-1832">
                    <span className="text-sm text-gray-300" data-id="element-1833">
                      Receipt Preview
                    </span>
                    <button type="button" onClick={() => setExpenseData({
                  ...expenseData,
                  receipt: null,
                  receiptPreview: null
                })} className="text-gray-400 hover:text-gray-200" data-id="element-1834">
                      <XIcon size={16} data-id="element-1835" />
                    </button>
                  </div>
                  <div className="mt-2 bg-gray-800 rounded-md p-2 flex items-center justify-center" data-id="element-1836">
                    <img src={expenseData.receiptPreview} alt="Receipt preview" className="max-h-40 object-contain" data-id="element-1837" />
                  </div>
                </div> : <div className={`border-2 border-dashed ${errors.receipt ? 'border-red-500' : 'border-gray-600'} rounded-lg p-6 text-center bg-gray-700`} data-id="element-1838">
                  <div className="flex flex-col items-center" data-id="element-1839">
                    <UploadIcon className="h-10 w-10 text-gray-500" data-id="element-1840" />
                    <p className="mt-2 text-sm text-gray-300" data-id="element-1841">
                      Drag and drop your receipt image, or{' '}
                      <label className="text-blue-400 hover:text-blue-300 cursor-pointer" data-id="element-1842">
                        browse
                        <input type="file" className="hidden" accept="image/*" onChange={handleReceiptUpload} data-id="element-1843" />
                      </label>
                    </p>
                    <p className="mt-1 text-xs text-gray-400" data-id="element-1844">
                      JPEG, PNG, or PDF up to 10MB
                    </p>
                    <button type="button" className="mt-4 inline-flex items-center px-3 py-2 border border-gray-600 text-sm leading-4 font-medium rounded-md text-gray-200 bg-gray-600 hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => document.getElementById('camera-input').click()} data-id="element-1845">
                      <CameraIcon size={16} className="mr-2" data-id="element-1846" />
                      Take Photo
                      <input id="camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={handleReceiptUpload} data-id="element-1847" />
                    </button>
                  </div>
                </div>}
              {errors.receipt && <p className="mt-1 text-sm text-red-500" data-id="element-1848">{errors.receipt}</p>}
            </div>
            {/* Policy Information */}
            <div className="bg-blue-900/30 border border-blue-800 rounded-md p-4 flex items-start" data-id="element-1849">
              <InfoIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-1850" />
              <div data-id="element-1851">
                <h3 className="text-sm font-medium text-blue-300" data-id="element-1852">
                  Expense Policy Information
                </h3>
                <p className="text-sm text-blue-200 mt-1" data-id="element-1853">
                  {expenseData.type === 'meal' && expenseData.subtype && perDiemRates && <>
                        The per diem limit for {expenseData.subtype} in{' '}
                        {expenseData.location} is $
                        {perDiemRates.mealBreakdown[expenseData.subtype]}.
                        Expenses exceeding this amount may not be fully
                        reimbursed.
                      </>}
                  {expenseData.type === 'lodging' && perDiemRates && <>
                      The per diem limit for lodging in {expenseData.location}{' '}
                      is ${perDiemRates.lodgingRate} per night. Expenses
                      exceeding this amount may not be fully reimbursed.
                    </>}
                  {(!expenseData.type || expenseData.type === 'meal' && !expenseData.subtype) && <>
                      Select an expense type to see policy information. All
                      expenses must comply with company policy and GSA per diem
                      rates.
                    </>}
                  {expenseData.type === 'transportation' && <>
                      Transportation expenses must be for business purposes. For
                      personal vehicle use, please submit mileage instead of
                      fuel costs.
                    </>}
                  {expenseData.type === 'other' && <>
                      Other expenses must be for business purposes and require a
                      detailed description. Receipts are required for all
                      expenses over $75.
                    </>}
                </p>
              </div>
            </div>
            {/* Warning if exceeding per diem */}
            {perDiemValidation && !perDiemValidation.valid && <div className="bg-red-900/30 border border-red-800 rounded-md p-4 flex items-start" data-id="element-1854">
                <AlertTriangleIcon className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-1855" />
                <div data-id="element-1856">
                  <h3 className="text-sm font-medium text-red-300" data-id="element-1857">
                    Exceeds Per Diem Limit
                  </h3>
                  <p className="text-sm text-red-200 mt-1" data-id="element-1858">
                    This expense exceeds the per diem limit of $
                    {perDiemValidation.allowance}. You may still submit the
                    expense, but please provide a detailed justification in the
                    description.
                  </p>
                </div>
              </div>}
          </div>
          <div className="bg-gray-750 px-6 py-4 flex justify-between items-center border-t border-gray-700" data-id="element-1859">
            <button type="button" onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')} className="text-gray-300 hover:text-gray-100" data-id="element-1860">
              Cancel
            </button>
            <div className="flex space-x-3" data-id="element-1861">
              <Button type="button" variant="secondary" onClick={() => {
              // Reset form but keep employee selection for admin
              setExpenseData({
                date: new Date().toISOString().split('T')[0],
                location: 'Washington, DC',
                type: '',
                subtype: '',
                amount: '',
                description: '',
                attendees: [],
                receipt: null,
                receiptPreview: null,
                employeeId: isAdmin ? expenseData.employeeId : '1'
              });
              setErrors({});
              setPerDiemValidation(null);
            }} data-id="element-1862">
                Reset
              </Button>
              <Button type="submit" disabled={isSubmitting} data-id="element-1863">
                {isSubmitting ? <>
                    <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-1864"></div>
                    Submitting...
                  </> : <>
                    <CheckIcon size={16} className="mr-1.5" data-id="element-1865" />
                    Submit Expense
                  </>}
              </Button>
            </div>
          </div>
        </Card>
      </form>
    </div>;
}